import { useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { ContractorAcceptRejectDialog } from "@/components/ContractorAcceptRejectDialog";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import {
  Send, DollarSign, CheckCircle, XCircle, MapPin, ArrowRight,
  ClipboardCheck, FileWarning, Shield, AlertTriangle, Camera,
  Calendar, ChevronRight, Wrench, Clock, Building2, ThumbsUp, ThumbsDown,
} from "lucide-react";
import { MyScheduleWidget } from "@/components/MyScheduleWidget";
import type { Job, ContractorOpportunity } from "@/data/types";
import { formatAddress } from "@/lib/addressUtils";
import { isApprovedForCrew } from "@/data/planningReview";

export function SubcontractorDashboard() {
  const { jobs, issues, opportunities, contractors } = useAppData();
  const [acceptRejectJob, setAcceptRejectJob] = useState<{ job: Job; opp: ContractorOpportunity } | null>(null);

  const mySub = contractors.find((c) => c.active);
  const myOpps = mySub ? opportunities.filter((o) => o.contractorId === mySub.id) : [];
  const pending = myOpps.filter((o) => o.responseStatus === "Pending" || o.responseStatus === "No Response");
  const accepted = myOpps.filter((o) => o.responseStatus === "Accepted");
  const bidSubmitted = myOpps.filter((o) => o.responseStatus === "Bid Submitted");
  const rejected = myOpps.filter((o) => o.responseStatus === "Rejected");
  const wonJobs = myOpps.filter((o) => o.isWinner);

  // Jobs pending acceptance = won but job dispatch is "Awaiting Response"
  const pendingAcceptance = wonJobs.filter((o) => {
    const job = jobs.find((j) => j.id === o.jobId);
    return job && job.dispatchStatus === "Awaiting Response" && o.responseStatus !== "Rejected";
  });

  const myActiveJobs = accepted
    .map((o) => jobs.find((j) => j.id === o.jobId))
    .filter(Boolean)
    // P12.4 — hide jobs pending project review from crew execution lists
    .filter((j) => isApprovedForCrew(j as Job)) as typeof jobs;

  const activeInstalls = myActiveJobs.filter((j) =>
    ["In Progress", "Assigned and Dispatched"].includes(j.currentStage)
  );
  const pendingQA = myActiveJobs.filter((j) => j.currentStage === "QA Review" && !j.qaSubmitted);

  const myIssues = issues.filter(
    (i) => myActiveJobs.some((j) => j.id === i.jobId) && (i.status === "Open" || i.status === "In Progress")
  );

  // Acceptance stats
  const totalResponded = accepted.length + rejected.length + bidSubmitted.length;
  const acceptanceRate = totalResponded > 0 ? Math.round(((accepted.length + bidSubmitted.length) / totalResponded) * 100) : 100;

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="max-w-2xl mx-auto pb-8 space-y-5">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-bold tracking-tight">Contractor Portal</h1>
          <p className="text-[11px] text-muted-foreground">
            {mySub?.companyName || "Contractor"} · {today}
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 border border-accent/20">
          <Building2 className="h-3.5 w-3.5 text-accent" />
          <span className="text-[10px] font-bold text-accent uppercase tracking-wider">Subcontractor</span>
        </div>
      </div>

      {/* ── My Schedule ── */}
      <MyScheduleWidget compact />

      {/* ── Compliance banner ── */}
      <div className="rounded-xl border-2 border-primary/20 bg-primary/[0.03] p-3.5">
        <div className="flex items-center gap-2 mb-1.5">
          <Shield className="h-4 w-4 text-primary" />
          <span className="text-xs font-bold text-primary uppercase tracking-wider">QA & Documentation Standard</span>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          All CCC partner contractors must submit the full Install QA checklist with before, progress, and completion photos.
          Jobs cannot close without 100% documentation compliance. Same standards as internal crews.
        </p>
      </div>

      {/* ── Status strip ── */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { label: "Pending", value: pendingAcceptance.length, icon: Clock, accent: "text-warning", bg: "bg-warning/10 border-warning/20" },
          { label: "Opportunities", value: pending.length, icon: Send, accent: "text-accent", bg: "bg-accent/10 border-accent/20" },
          { label: "Active Jobs", value: activeInstalls.length, icon: Wrench, accent: "text-info", bg: "bg-info/10 border-info/20" },
          { label: "QA Pending", value: pendingQA.length, icon: ClipboardCheck, accent: "text-warning", bg: "bg-warning/10 border-warning/20" },
          { label: "Issues", value: myIssues.length, icon: AlertTriangle, accent: "text-destructive", bg: "bg-destructive/10 border-destructive/20" },
        ].map((s) => (
          <div key={s.label} className={`rounded-xl border p-2.5 text-center ${s.bg}`}>
            <s.icon className={`h-3.5 w-3.5 mx-auto mb-0.5 ${s.accent}`} />
            <p className="text-lg font-bold leading-none">{s.value}</p>
            <p className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Acceptance Rate ── */}
      <div className="rounded-xl border p-3 bg-muted/20">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Your Acceptance Rate</span>
          <span className={`text-sm font-bold ${acceptanceRate >= 80 ? "text-success" : acceptanceRate >= 60 ? "text-warning" : "text-destructive"}`}>
            {acceptanceRate}%
          </span>
        </div>
        <Progress value={acceptanceRate} className="h-1.5" />
        <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3 text-success" />{accepted.length + bidSubmitted.length} accepted/bid</span>
          <span className="flex items-center gap-1"><ThumbsDown className="h-3 w-3 text-destructive" />{rejected.length} rejected</span>
          <span>{totalResponded} total responses</span>
        </div>
      </div>

      {/* ── Pending Acceptance (URGENT) ── */}
      {pendingAcceptance.length > 0 && (
        <section>
          <SectionHeader icon={<Clock className="h-4 w-4 text-warning" />} title="Pending Acceptance" count={pendingAcceptance.length} urgent />
          <div className="space-y-2">
            {pendingAcceptance.map((opp) => {
              const job = jobs.find((j) => j.id === opp.jobId);
              if (!job) return null;
              return (
                <div key={opp.id}>
                  <button
                    onClick={() => setAcceptRejectJob({ job, opp })}
                    className="w-full text-left p-3.5 rounded-xl border-2 border-warning/30 bg-warning/[0.04] hover:border-warning/50 active:scale-[0.98] transition-all"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-primary">{job.id}</span>
                      <StatusBadge type="dispatch" value="Awaiting Response" />
                    </div>
                    <p className="text-sm font-semibold">{job.projectType}</p>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                      <MapPin className="h-3 w-3" /> {formatAddress(job.address)}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                      <Calendar className="h-3 w-3" /> {job.scheduledInstallDate || job.targetInstallDate || "TBD"}
                      {job.timeSlot && ` · ${job.timeSlot}`}
                    </div>
                    {opp.bidAmount && (
                      <div className="flex items-center gap-1 text-[11px] font-semibold text-success mt-0.5">
                        <DollarSign className="h-3 w-3" /> ${opp.bidAmount.toLocaleString()} (your bid)
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-warning/20">
                      <span className="text-[10px] text-warning font-bold uppercase tracking-wider">Action Required</span>
                      <span className="text-xs text-warning font-bold flex items-center gap-1">
                        Accept or Decline <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Primary actions ── */}
      <div className="grid grid-cols-2 gap-3">
        {/* P12.2: QA reached via execution-first flow only. */}
        {(pendingQA[0] || activeInstalls[0]) ? (
          <Link
            to={`/installer-execution/${(pendingQA[0] ?? activeInstalls[0]).id}`}
            className="block"
          >
            <button className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-transform shadow-md">
              <ClipboardCheck className="h-5 w-5" />
              Open Next Job
            </button>
          </Link>
        ) : (
          <button
            disabled
            className="w-full py-4 rounded-xl bg-muted text-muted-foreground font-bold text-sm flex items-center justify-center gap-2"
          >
            <ClipboardCheck className="h-5 w-5" />
            No Active Jobs
          </button>
        )}
        <Link to="/issue-form" className="block">
          <button className="w-full py-4 rounded-xl bg-destructive/10 border-2 border-destructive/30 text-destructive font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-transform">
            <FileWarning className="h-5 w-5" />
            Escalate Issue
          </button>
        </Link>
      </div>

      {/* ── New Opportunities ── */}
      {pending.length > 0 && (
        <section>
          <SectionHeader icon={<Send className="h-4 w-4 text-accent" />} title="New Opportunities" count={pending.length} urgent />
          <div className="space-y-2">
            {pending.map((opp) => {
              const job = jobs.find((j) => j.id === opp.jobId);
              if (!job) return null;
              return (
                <Link key={opp.id} to={`/installer-execution/${job.id}`} className="block">
                  <div className="p-3.5 rounded-xl border-2 border-accent/20 bg-accent/[0.03] hover:border-accent/40 active:scale-[0.98] transition-all">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-primary">{job.id}</span>
                      <span className="text-[10px] text-muted-foreground">Sent {opp.sentDate}</span>
                    </div>
                    <p className="text-sm font-semibold">{job.projectType}</p>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                      <MapPin className="h-3 w-3" /> {formatAddress(job.address)}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{job.scopeSummary}</p>
                    <div className="flex items-center justify-end mt-2">
                      <span className="text-xs text-accent font-bold flex items-center gap-1">
                        Review & Respond <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Active Jobs ── */}
      {activeInstalls.length > 0 && (
        <section>
          <SectionHeader icon={<Wrench className="h-4 w-4 text-info" />} title="Active Jobs" count={activeInstalls.length} />
          <div className="space-y-2">
            {activeInstalls.map((j) => {
              const jobIssues = issues.filter((i) => i.jobId === j.id && i.status === "Open");
              const docProgress = j.qaSubmitted ? 100 : j.currentStage === "In Progress" ? 40 : 0;
              return (
                <Link key={j.id} to={`/installer-execution/${j.id}`} className="block">
                  <Card className="border hover:border-primary/30 active:scale-[0.99] transition-all">
                    <CardContent className="p-3.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-primary">{j.id}</span>
                          <StatusBadge type="stage" value={j.currentStage} />
                        </div>
                        {j.scheduledInstallDate && (
                          <span className="text-[10px] text-muted-foreground">{j.scheduledInstallDate}</span>
                        )}
                      </div>
                      <p className="text-sm font-semibold">{j.customerName}</p>
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                        <MapPin className="h-3 w-3 shrink-0" /> {formatAddress(j.address)}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{j.projectType} · {j.scopeSummary}</p>
                      <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t">
                        <div className="flex items-center gap-1.5 flex-1">
                          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Docs</span>
                          <Progress value={docProgress} className="h-1.5 flex-1" />
                          <span className="text-[9px] font-bold text-muted-foreground">{docProgress}%</span>
                        </div>
                        {jobIssues.length > 0 && (
                          <div className="flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3 text-destructive" />
                            <span className="text-[10px] font-bold text-destructive">{jobIssues.length}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ── QA Pending ── */}
      {pendingQA.length > 0 && (
        <section>
          <SectionHeader icon={<ClipboardCheck className="h-4 w-4 text-warning" />} title="QA Required" count={pendingQA.length} urgent />
          <div className="space-y-2">
            {pendingQA.map((j) => (
              <Link key={j.id} to={`/installer-execution/${j.id}`} className="block">
                <div className="flex items-center justify-between p-3.5 rounded-xl border-2 border-warning/30 bg-warning/5 active:scale-[0.98] transition-transform">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-warning/20 flex items-center justify-center">
                      <Camera className="h-5 w-5 text-warning" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{j.id} — {j.customerName}</p>
                      <p className="text-[10px] text-muted-foreground">QA checklist incomplete</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-warning" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── Rejection History ── */}
      {rejected.length > 0 && (
        <section>
          <SectionHeader icon={<XCircle className="h-4 w-4 text-destructive" />} title="Declined Jobs" count={rejected.length} />
          <div className="space-y-2">
            {rejected.map((opp) => {
              const job = jobs.find((j) => j.id === opp.jobId);
              return (
                <div key={opp.id} className="p-3 rounded-xl border bg-muted/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-muted-foreground">{opp.jobId}</span>
                    <span className="text-[10px] text-muted-foreground">{opp.rejectedDate || opp.responseDate}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{job?.projectType}</p>
                  {opp.rejectionReason && (
                    <p className="text-[11px] text-muted-foreground mt-1 italic">Reason: {opp.rejectionReason}</p>
                  )}
                  {opp.bidNotes && !opp.rejectionReason && (
                    <p className="text-[11px] text-muted-foreground mt-1 italic">{opp.bidNotes}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Pending Bids ── */}
      {bidSubmitted.length > 0 && (
        <section>
          <SectionHeader icon={<DollarSign className="h-4 w-4 text-info" />} title="Pending Bids" count={bidSubmitted.length} />
          <div className="space-y-2">
            {bidSubmitted.map((opp) => {
              const job = jobs.find((j) => j.id === opp.jobId);
              return (
                <div key={opp.id} className="flex items-center justify-between p-3 rounded-xl border">
                  <div>
                    <Link to={`/installer-execution/${opp.jobId}`} className="text-xs font-bold text-primary hover:underline">{opp.jobId}</Link>
                    <p className="text-[10px] text-muted-foreground">{job?.projectType}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{opp.bidAmount ? `$${opp.bidAmount.toLocaleString()}` : "—"}</p>
                    <StatusBadge type="contractorResponse" value={opp.responseStatus} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Empty state ── */}
      {myOpps.length === 0 && myActiveJobs.length === 0 && (
        <div className="py-16 text-center">
          <Send className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No opportunities yet</p>
          <p className="text-xs text-muted-foreground mt-1">New job opportunities will appear here when sent by CCC.</p>
        </div>
      )}

      {/* Accept/Reject Dialog */}
      {acceptRejectJob && (
        <ContractorAcceptRejectDialog
          open={!!acceptRejectJob}
          onOpenChange={(v) => { if (!v) setAcceptRejectJob(null); }}
          job={acceptRejectJob.job}
          opportunity={acceptRejectJob.opp}
        />
      )}
    </div>
  );
}

function SectionHeader({ icon, title, count, urgent }: { icon: React.ReactNode; title: string; count: number; urgent?: boolean }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-1">
      {icon}
      <h2 className="text-sm font-bold tracking-tight">{title}</h2>
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
        urgent ? "bg-warning/20 text-warning" : "bg-muted text-muted-foreground"
      }`}>{count}</span>
    </div>
  );
}
